import { Illustration, Text, Title } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";

import { BackgroundCheckInfoSection } from "./BackgroundCheckInfoSection";
import { BackgroundCheckPageContainer } from "./BackgroundCheckPageContainer";

interface BackgroundCheckIntroPageProps {
  isLoading: boolean;
  onContinue: () => void;
}

export function BackgroundCheckIntroPage(props: BackgroundCheckIntroPageProps) {
  const { isLoading, onContinue } = props;

  useLogEffect(APP_V2_APP_EVENTS.BACKGROUND_CHECK_INTRO_PAGE_VIEWED);

  return (
    <BackgroundCheckPageContainer
      buttonText="Start background check"
      isLoading={isLoading}
      onButtonClick={onContinue}
    >
      <Stack spacing={8} sx={{ alignItems: "center", textAlign: "center", mb: 8 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Illustration type="document-review" />
        </Box>

        <Stack spacing={4}>
          <Title variant="h2" component="h2">
            Last step before booking!
          </Title>
          <Text variant="h5">
            We need to run a national background check before confirming your shift
          </Text>
        </Stack>
      </Stack>

      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.primary,
          borderRadius: 4,
          p: 7,
        }}
      >
        <Stack spacing={4}>
          <BackgroundCheckInfoSection title="How it works">
            <Text variant="body1" sx={{ color: (theme) => theme.palette.text.secondary }}>
              A background check takes about a minute to submit and is usually processed within a
              couple of hours.
            </Text>
          </BackgroundCheckInfoSection>

          <Text variant="body1" sx={{ color: (theme) => theme.palette.text.secondary }}>
            <Box component="span" sx={{ fontWeight: "bolder" }}>
              You can keep booking shifts while it&rsquo;s processing.
            </Box>{" "}
            In the rare case there&rsquo;s a delay or issue, we may need to pause or remove you from
            shifts until it&rsquo;s resolved &mdash; we&rsquo;ll notify you right away if that
            happens.
          </Text>

          <BackgroundCheckInfoSection title="Why it's needed">
            <Text variant="body1" sx={{ color: (theme) => theme.palette.text.secondary }}>
              It&rsquo;s required to determine your eligibility to work shifts on the Clipboard
              platform.
            </Text>
          </BackgroundCheckInfoSection>
        </Stack>
      </Box>
    </BackgroundCheckPageContainer>
  );
}
